<template>
  <div class="demo-viewer-container">
    <div :id="panoId" class="pano" />
    <NuxtImg
      v-show="!marzipanoReady && props.imageSrc"
      :src="marzipanoPreviewImage"
      loading="eager"
      fetchpriority="high"
      height="100%"
      width="100%"
      class="marzipano_cover_preview"
    />
  </div>
</template>
<script setup>
import logger from "debug";
import { onMounted } from "vue";
const debug = logger("travvir:debut-nuxt:viewer:DemoViewer");

//variables
const viewers = reactive({});
const marzipanoReady = ref(false);
const emit = defineEmits(["marzipanoReady"]);
const { $isAppleChrome, $isAppleWebview, $isIPhone, $isMacSafari, $marzipano } =
  useNuxtApp();

const props = defineProps({
  panoId: {
    type: String,
    default: "explorePano1",
  },
  imageSrc: {
    type: String,
    required: true,
  },
  drag: {
    type: Boolean,
    default: false,
  },
  scrollZoom: {
    type: Boolean,
    default: true,
  },
  onlyPreview: {
    type: Boolean,
    default: false,
  },
  customFov: {
    type: Boolean,
    default: false,
  },
  marzpanoVisibleDelay: {
    type: Number,
    default: 0,
  },
});

//watchers
watch(
  () => props.imageSrc,
  async (newSrc, oldSrc) => {
    debug("Image source changed:", newSrc);
    if (newSrc !== oldSrc) {
      await initPano();
    }
  },
);

//computed
const marzipanoPreviewImage = computed(() => {
  if (!props.imageSrc) return;
  return `${props.imageSrc}/preview_high.webp`;
});

//methods
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      debug("loaded image", url);
      resolve();
      emit("marzipanoReady", true);
    };
    img.onerror = (error) => {
      debug("Failed to load image:", url, error);
      reject(new Error(`Failed to load image: ${url}`));
      emit("marzipanoReady", false);
    };
    img.src = url;
  });
};

const loadPanorama = async (viewer, imageSource, panoElement) => {
  try {
    const levels = [
      {
        tileSize: 256,
        size: 256,
        fallbackOnly: true,
      },
      {
        tileSize: 512,
        size: 512,
      },
      {
        tileSize: 512,
        size: 1024,
      },
      {
        tileSize: 512,
        size: 2048,
      },
    ];
    let sequence = "bdflru";
    if ($isAppleChrome || $isAppleWebview || $isMacSafari || $isIPhone) {
      sequence = "urlfdb";
    }

    const geometry = new $marzipano.CubeGeometry(levels);
    const source = $marzipano.ImageUrlSource.fromString(
      `${imageSource}/{z}/{f}/{y}/{x}.jpg`,
      {
        cubeMapPreviewUrl: `${imageSource}/preview.jpg`,
        cubeMapPreviewFaceOrder: sequence,
      },
    );

    const initialView = {
      fov: props.customFov ? (59 * Math.PI) / 180 : (90 * Math.PI) / 180,
    };
    const view = new $marzipano.RectilinearView(initialView);

    const scene = viewer.createScene({
      source: source,
      geometry: geometry,
      view: view,
      pinFirstLevel: true,
    });

    scene.switchTo({ transitionDuration: 1000 });
    viewer.controls().disable();
    debug("Loading preview image for:", imageSource);
    await loadImage(`${imageSource}/preview.jpg`);

    const resizeObserver = new ResizeObserver(() => {
      viewer.updateSize();
    });
    resizeObserver.observe(panoElement);
    var autorotate = $marzipano.autorotate({
      yawSpeed: 0.15,
      targetPitch: 0,
    });
    viewer.startMovement(autorotate);
  } catch (error) {
    debug("Error in loadPanorama:", error);
    throw error;
  }
};

const initPano = async () => {
  marzipanoReady.value = false;
  const panoElement = document.getElementById(props.panoId);
  debug("Canvas Container element:", panoElement);

  if (!panoElement) {
    debug("No Canvas Container element found");
    return;
  }
  if (!props.imageSrc) {
    debug("No image source provided for panorama");
    return;
  }

  try {
    const imageSource = props.imageSrc;
    const viewerOpts = {
      controls: { mouseViewMode: "drag", scrollZoom: props.scrollZoom },
    };
    const viewer = new $marzipano.Viewer(panoElement, viewerOpts);
    await loadPanorama(viewer, imageSource, panoElement);
    debug(`Successfully loaded panorama: ${viewer}`);
  } catch (error) {
    debug(`Failed to load panorama:`, error);
  }
  if (props.marzpanoVisibleDelay > 0) {
    setTimeout(() => {
      marzipanoReady.value = true;
    }, props.marzpanoVisibleDelay);
  } else {
    marzipanoReady.value = true;
    debug(marzipanoReady.value, "ready", viewers);
  }
};

onMounted(async () => {
  await initPano();
});
</script>

<style scoped>
.demo-viewer-container {
  border-radius: inherit;
  width: 100%;
  height: 100%;
  .pano {
    border-radius: inherit;
    width: 100%;
    height: 100%;
  }
  img {
    border-radius: inherit;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
}
</style>
